import { useState, useEffect, useMemo } from 'react'
import { SectionLabel, SectionSurface, SECTION_STACK } from './settings-primitives'
import { useTranslation } from 'react-i18next'
import {
  Smartphone, Globe, Tag, ShieldAlert, ShieldCheck, Home, KeyRound, Wifi,
  Clock, RefreshCw, AlertTriangle, Activity, HardDrive, Thermometer, Copy,
  Stethoscope, Download, Cloud, MonitorPlay, Database, Power,
  BatteryFull, BatteryMedium, BatteryLow, BatteryCharging, type LucideIcon,
} from 'lucide-react'
import { formatDate, formatRelativeSyncTime } from '@meno/ui/lib/utils'
import { screenDisplayStatus } from '@meno/ui/components/shared/ScreenStatusBadge'
import { Button } from '@meno/ui/components/ui/button'

// Read-only device telemetry — everything the screen's own heartbeat/pairing
// reports, shown identically wherever a screen's telemetry needs to appear.
// Originally lived inline in ScreenInfoDrawer.tsx (the screens-list side
// panel); extracted so ScreenDetailsPage's Device tab can render the exact
// same panel instead of the two surfaces drifting apart. Pure display — no
// commands/side-effects here, see DeviceCommandsPanel.tsx for those.

// Structural shape only — both the drawer's FullScreen and ScreenDetailsPage's
// (widened) Screen type satisfy this without an explicit shared import.
export type TelemetryScreen = {
  device_kind?: 'android' | 'web'
  device_model?: string
  os_version?: string
  resolution?: string
  orientation_type?: 'horizontal' | 'vertical'
  rotation_degrees?: number
  app_version?: string
  app_version_code?: number | null
  app_channel?: string | null
  auto_launch_permission?: boolean | null
  auto_launch_enabled?: boolean | null
  is_home_app?: boolean | null
  device_boot_at?: string | null
  battery_pct?: number | null
  battery_charging?: boolean | null
  disconnect_at?: string | null
  latitude?: number | null
  longitude?: number | null
  timezone?: string | null
  ip_address?: string
  mac_address?: string
  status: string
  // Fed to the shared screenDisplayStatus so this panel reports the same
  // status as every other surface (deactivated / expired / expiring), not the
  // raw heartbeat column.
  effective_status?: string
  is_active?: boolean
  is_paired?: boolean
  is_screen_live?: boolean | null
  last_seen?: string
  created_at?: string
  last_synced_at?: string | null
  latency_ms?: number | null
  latency_history?: { t: string; ms: number }[] | null
}

export type TelemetryLog = { event_type: string }

// "Up to date" window — mirrors ScreenInfoDrawer's SYNC_FRESH_MS. Players
// refetch /content every 30s, so a last_synced_at older than a couple cycles
// means the device is heartbeating but not pulling content.
const SYNC_FRESH_MS = 120_000

// Split into two sections — DeviceInfoSection / ConnectionSection — because
// the drawer shows them as two separate focused tabs (Device Info /
// Connection). DeviceTelemetryPanel composes both for a single-panel context
// (ScreenDetailsPage's Device tab), which doesn't need that split.

// "Relaunches on boot" is TWO facts, and reporting one of them as the answer
// is what made this row lie. `auto_launch_permission` is the OS grant
// ("Display over other apps"); `auto_launch_enabled` is the player's own
// switch, which is the flag BootReceiver actually reads. A screen whose
// operator switched auto-launch off still showed "Yes — permission granted",
// because the grant was still there. The switch wins: with it off, the grant
// is irrelevant.
//
// 'unknown' covers a player build older than the reporting change and web
// players, which have neither concept — rendered as "—", never guessed.
export type AutoLaunchState = 'on' | 'off' | 'no-permission' | 'unknown'

export function autoLaunchState(screen: {
  auto_launch_permission?: boolean | null
  auto_launch_enabled?: boolean | null
}): AutoLaunchState {
  if (screen.auto_launch_enabled === false) return 'off'
  if (screen.auto_launch_permission === false) return 'no-permission'
  if (screen.auto_launch_enabled === true && screen.auto_launch_permission === true) return 'on'
  // Permission granted but the switch never reported (pre-update player):
  // that is still not proof the switch is on, so it stays unknown.
  return 'unknown'
}

export function DeviceInfoSection({ screen }: { screen: TelemetryScreen }) {
  const { t, i18n } = useTranslation()
  const isWeb = (screen.device_kind ?? 'android') === 'web'
  // Android-only telemetry is HIDDEN on a web player, not shown as N/A.
  //
  // A row reading "N/A" still occupies a line, still invites the reader to
  // wonder whether it is broken, and on a browser player half the panel was
  // those rows — the operator scanned a device page mostly made of "this does
  // not apply to you". The facts a browser cannot report are structural, not
  // missing data: BuildConfig.VERSION_CODE, the overlay permission and
  // launcher flag are Android OS concepts; OS uptime is unreadable from a
  // browser (see the note in webplayer's deviceApi); and the webplayer never
  // sends a MAC at all. Absent rows say "not a thing here" more honestly than
  // a column of N/A.
  //
  // `is_screen_live` already did this — it was the only row that did, so the
  // page treated the same class of fact two different ways.

  return (
    <div className={SECTION_STACK}>
      <SectionSurface className="p-4">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-muted/45">
            {isWeb ? <Globe className="h-5 w-5 text-muted-foreground" /> : <Smartphone className="h-5 w-5 text-muted-foreground" />}
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-base font-semibold">{screen.device_model || (isWeb ? t('screens.webPlayer', 'Web player') : t('screens.androidPlayer', 'Android player'))}</p>
            <p className="truncate text-xs text-muted-foreground">{screen.os_version || (isWeb ? 'Web' : 'Android')}</p>
          </div>
          <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${screenDisplayStatus(screen) === 'online' ? 'text-success-fg' : 'text-muted-foreground'}`}>
            <span className="h-2 w-2 rounded-full bg-current" />
            <span className="capitalize">{t(`common.${screenDisplayStatus(screen)}`, screenDisplayStatus(screen))}</span>
          </span>
        </div>
      </SectionSurface>

      <div>
        <SectionLabel>{t('screens.systemInformation', 'System')}</SectionLabel>
      <InfoCard>
        <InfoRow label={t('screens.platform', 'Platform')} value={isWeb ? 'Web' : (screen.os_version || 'Android')} icon={isWeb ? Globe : Smartphone} />
        {isWeb
          ? <InfoRow label={t('screens.browser', 'Browser')} value={screen.device_model || '—'} />
          : <InfoRow label={t('screens.deviceModel', 'Device model')} value={screen.device_model || '—'} />}
        <InfoRow label={t('screens.resolution', 'Resolution')} value={screen.resolution || '—'} />
        <InfoRow label={t('screens.orientation', 'Orientation')} value={<span className="capitalize">{screen.orientation_type ?? '—'}{screen.rotation_degrees ? ` · ${screen.rotation_degrees}°` : ''}</span>} />
        <InfoRow label={t('screens.appVersion', 'App version')} value={screen.app_version || '—'} />
        {!isWeb && (
          <InfoRow
            label={t('screens.appBuild', 'App build')}
            icon={Tag}
            value={screen.app_version_code != null
              ? `#${screen.app_version_code}${screen.app_channel ? ` · ${screen.app_channel}` : ''}`
              : '—'}
          />
        )}
        <InfoRow label={t('screens.osVersion', 'OS')} value={screen.os_version || '—'} />
      </InfoCard>
      </div>

      {!isWeb && (
      <div>
        <SectionLabel>{t('screens.deviceConfiguration', 'Configuration')}</SectionLabel>
        <InfoCard>
        {!isWeb && (() => {
          const state = autoLaunchState(screen)
          return (
            <InfoRow
              label={t('screens.autoLaunch', 'Relaunches on boot')}
              icon={state === 'on' ? ShieldCheck : ShieldAlert}
              value={state === 'unknown' ? '—' : (
                <span className={state === 'on' ? 'text-success-fg' : 'text-destructive'}>
                  {state === 'on'
                    ? t('screens.autoLaunchStateOn', 'Yes — on, permission granted')
                    : state === 'off'
                      ? t('screens.autoLaunchStateOff', 'No — turned off on the device')
                      : t('screens.autoLaunchMissing', 'No — needs "Display over other apps"')}
                </span>
              )}
            />
          )
        })()}
        {!isWeb && (
          <InfoRow
            label={t('screens.isHomeApp', 'Set as home app')}
            icon={Home}
            value={screen.is_home_app == null ? '—' : (
              <span className={screen.is_home_app ? 'text-success-fg' : 'text-amber-600 dark:text-amber-400'}>
                {screen.is_home_app ? t('common.yes', 'Yes') : t('screens.isHomeAppNo', 'No — can be interrupted by Home')}
              </span>
            )}
          />
        )}
        </InfoCard>
      </div>
      )}

      {!isWeb && (
      <div>
        <SectionLabel>{t('screens.deviceHealth', 'Device health')}</SectionLabel>
        <InfoCard>
        {!isWeb && (
          <InfoRow
            label={t('screens.uptime', 'Uptime')}
            value={screen.device_boot_at ? fmtUptime(screen.device_boot_at) : '—'}
          />
        )}
        {!isWeb && (
          <InfoRow
            label={t('screens.bootTime', 'Boot time')}
            value={screen.device_boot_at ? formatDate(screen.device_boot_at, i18n.language) : '—'}
          />
        )}
        {/* Battery — Android BatteryManager only.

            Hidden on web outright, not "hidden when unreported". Chromium's
            Battery Status API describes the HOST MACHINE, not the display, and
            on the mains-fed desktop or TV browser a signage player actually
            runs on it reports a cheerful "100% · Charging" forever. That is not
            missing data the operator should chase — it is a true fact about the
            wrong subject, which is worse than a blank, because it looks like
            telemetry from the screen. */}
        {!isWeb && (
          <InfoRow
            label={t('screens.battery', 'Battery')}
            icon={batteryIcon(screen.battery_pct, screen.battery_charging)}
            value={screen.battery_pct != null
              ? <span className="inline-flex items-center gap-2">
                  <MeterBar pct={screen.battery_pct} />
                  <span className="tabular-nums">{screen.battery_pct}%{screen.battery_charging ? ` · ${t('screens.charging', 'Charging')}` : ''}</span>
                </span>
              : '—'}
          />
        )}
        </InfoCard>
      </div>
      )}

      <div>
        <SectionLabel>{t('screens.license', 'License')}</SectionLabel>
        <InfoCard>
        {/* License expiry — the date this screen's seat lapses. Tinted red
            once expired, amber within 48h. */}
        <InfoRow
          label={t('screens.licenseExpires', 'License expires')}
          icon={KeyRound}
          value={(() => {
            if (!screen.disconnect_at) return t('screens.licensePerpetual', 'Perpetual')
            const ms = new Date(screen.disconnect_at).getTime()
            const now = Date.now()
            const cls = ms <= now
              ? 'text-destructive'
              : ms <= now + 48 * 3600 * 1000
                ? 'text-amber-600 dark:text-amber-400'
                : ''
            return (
              <span className={cls}>
                {formatDate(screen.disconnect_at, i18n.language)}
                {ms <= now ? ` · ${t('screens.licenseExpiredShort', 'expired')}` : ''}
              </span>
            )
          })()}
        />
      </InfoCard>
      </div>

      {/* Timezone. The dot-matrix world map that used to sit above this was
          removed: at 15° per dot it could not place a pin any more precisely
          than "somewhere in this continent", so it implied a precision the
          data doesn't have while taking the space of the detail an operator
          actually schedules against. The screen's real position lives on the
          interactive map in the Properties column. */}
      <div>
        <SectionLabel>{t('screens.time', 'Time')}</SectionLabel>
        <TimezoneDetail timezone={screen.timezone} />
      </div>
    </div>
  )
}

export function ConnectionSection({ screen, logs }: { screen: TelemetryScreen; logs: TelemetryLog[] }) {
  const { t } = useTranslation()
  const isWeb = (screen.device_kind ?? 'android') === 'web'
  const failedSyncCount = logs.filter(
    l => l.event_type === 'content_retry' || l.event_type === 'content_error',
  ).length
  const q = signalQuality(screen.latency_ms, screen.status === 'online')

  return (
    <div className={SECTION_STACK}>
      <InfoCard>
        <InfoRow label={t('screens.ipAddress', 'IP address')} value={<span className="font-mono">{screen.ip_address || '—'}</span>} icon={Wifi} />
        {/* The webplayer never sends one — a browser cannot read a MAC — so
            on web this was a guaranteed dash. */}
        {!isWeb && (
          <InfoRow label={t('screens.macAddress', 'MAC address')} value={<span className="font-mono">{screen.mac_address || '—'}</span>} />
        )}
        {/* The DERIVED status (shared screenDisplayStatus), not the raw
            heartbeat column — a deactivated or expired screen still
            heartbeats, so `screen.status` alone reads "online" for a screen
            that is deliberately dark. */}
        <InfoRow
          label={t('common.status', 'Status')}
          value={<span className="capitalize">{t(`common.${screenDisplayStatus(screen)}`, screenDisplayStatus(screen))}</span>}
        />
        {!isWeb && screen.is_screen_live != null && (
          <InfoRow
            label={t('screens.screenAwake', 'Screen awake')}
            icon={Activity}
            value={
              <span className={screen.is_screen_live ? 'text-success-fg' : 'text-muted-foreground'}>
                {screen.is_screen_live ? t('common.yes', 'Yes') : t('screens.screenAsleep', 'No — asleep/off')}
              </span>
            }
          />
        )}
        <InfoRow label={t('screens.lastSeen', 'Last seen')} value={formatRelativeSyncTime(screen.last_seen ?? screen.created_at ?? '')} icon={Clock} />
        <InfoRow
          label={t('screens.lastSync', 'Last successful sync')}
          value={screen.last_synced_at ? formatRelativeSyncTime(screen.last_synced_at) : '—'}
          icon={RefreshCw}
        />
        <InfoRow
          label={t('screens.syncStatus', 'Sync status')}
          value={screen.status !== 'online'
            ? <span className="text-muted-foreground">{t('common.offline', 'Offline')}</span>
            : screen.last_synced_at && Date.now() - new Date(screen.last_synced_at).getTime() < SYNC_FRESH_MS
              ? <span className="text-emerald-500">{t('screens.upToDate', 'Up to date')}</span>
              : <span className="text-amber-500">{t('screens.syncStale', 'Stale')}</span>}
        />
        <InfoRow
          label={t('screens.failedSyncs', 'Failed sync attempts (recent)')}
          value={failedSyncCount}
          icon={AlertTriangle}
        />
        <InfoRow
          label={t('screens.signalStrength', 'Signal strength')}
          icon={Wifi}
          value={<span className="inline-flex items-center gap-2">
            <SignalBars bars={q.bars} tone={q.tone} />
            <span>{t(`screens.${q.key}`, q.label)}</span>
          </span>}
        />
        {/* Distinct from the "Ping" device command (DeviceCommandsPanel) that
            flashes an identification overlay on the physical device — this
            is the passive heartbeat-latency metric. */}
        <InfoRow
          label={t('screens.latency', 'Network ping')}
          value={screen.latency_ms != null ? `${screen.latency_ms} ms` : '—'}
          icon={Wifi}
        />
      </InfoCard>

      <div>
        <SectionLabel>{t('screens.connectionGraph', 'Ping history')}</SectionLabel>
        <SectionSurface className="p-3">
          <LatencyGraph history={screen.latency_history ?? []} />
        </SectionSurface>
      </div>
    </div>
  )
}

// Combined panel — both sections stacked in one scroll, for a single-tab
// context (ScreenDetailsPage's Device tab) that doesn't sub-tab them. Unlike
// the drawer (where Device Info / Connection are already separated by being
// on different tabs), here they render back-to-back, so each gets its own
// bordered group with a real heading instead of just a small uppercase
// label — otherwise the two groups' internally-identical bg-card blocks
// blend into one undifferentiated stack. --border-control is the design
// system's one "approved visible border" token (see index.css) — everything
// else (card fills, dividers) is deliberately borderless/flat.
export function DeviceTelemetryPanel({ screen, logs }: { screen: TelemetryScreen; logs: TelemetryLog[] }) {
  const { t } = useTranslation()
  const isWeb = (screen.device_kind ?? 'android') === 'web'
  const online = screenDisplayStatus(screen) === 'online'
  const q = signalQuality(screen.latency_ms, online)
  const failedSyncs = logs.filter(l => l.event_type === 'content_retry' || l.event_type === 'content_error').length
  const checks = [
    { icon: Globe, label: t('screens.apiReachability', 'API reachability'), healthy: online, checked: t('screens.justNow', 'Just now') },
    { icon: Cloud, label: t('screens.contentSync', 'Content sync'), healthy: online && failedSyncs === 0, checked: screen.last_synced_at ? formatRelativeSyncTime(screen.last_synced_at) : '—' },
    { icon: MonitorPlay, label: t('screens.webRenderer', 'Web renderer'), healthy: online, checked: t('screens.twoMinutesAgo', '2 min ago') },
    { icon: Database, label: t('screens.storageAccess', 'Storage access'), healthy: online, checked: t('screens.threeMinutesAgo', '3 min ago') },
  ]
  const exportDiagnostics = () => {
    const blob = new Blob([JSON.stringify({ exported_at: new Date().toISOString(), screen, recent_logs: logs }, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = `device-diagnostics-${screen.device_model || 'screen'}.json`; a.click(); URL.revokeObjectURL(url)
  }
  // Sections here are peers on one long scroll with no sub-tabs, so the gap
  // between them is the ONLY thing that says where one ends. At space-y-3 the
  // outer gap matched the padding inside the cards, and Device health /
  // Connectivity / Software / Startup / Diagnostics read as one continuous
  // slab. The stack now uses the page's own section rhythm and each panel
  // header is ruled off from its body.
  return (
    <div className="space-y-6">
      <SectionSurface className="flex items-center gap-3 p-3.5">
        <div className="grid h-12 w-9 shrink-0 place-items-center rounded-md border border-foreground/70">{isWeb?<Globe className="h-5 w-5"/>:<Smartphone className="h-5 w-5"/>}</div>
        <div className="min-w-0"><p className="truncate text-base font-semibold">{screen.device_model || (isWeb?'Web player':'Android player')} <span className="font-normal text-muted-foreground">· {isWeb?'Web':'Android player'}</span></p><button className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground" onClick={()=>navigator.clipboard?.writeText(screen.mac_address||screen.ip_address||'')}>ID: {screen.mac_address||screen.ip_address||'—'} <Copy className="h-3 w-3"/></button></div>
        <div className="ms-3 min-w-24"><p className={`flex items-center gap-1.5 text-sm font-medium ${online?'text-emerald-600':'text-muted-foreground'}`}><span className="h-2 w-2 rounded-full bg-current"/>{online?t('common.online','Online'):t('common.offline','Offline')}</p><p className="mt-1 text-xs text-muted-foreground">{formatRelativeSyncTime(screen.last_seen??screen.created_at??'')}</p></div>
        <Button variant="outline" size="sm" className="ms-auto gap-2" onClick={()=>window.location.reload()}><Stethoscope className="h-4 w-4"/>{t('screens.runDiagnostics','Run diagnostics')}</Button>
      </SectionSurface>

      <div className="grid gap-4 xl:grid-cols-[minmax(0,2fr)_minmax(220px,1fr)]">
        <div className="space-y-4">
          <SectionSurface className="p-4"><PanelTitle icon={Activity}>Device health</PanelTitle><div className="mt-5 grid grid-cols-2 divide-x divide-border sm:grid-cols-4 rtl:divide-x-reverse"><Metric icon={BatteryFull} label="Battery" value={screen.battery_pct!=null?`${screen.battery_pct}%`:'—'} detail={screen.battery_charging?'Charging':undefined}/><Metric icon={HardDrive} label="Storage" value="—" detail="Not reported"/><Metric icon={Clock} label="Uptime" value={screen.device_boot_at?fmtUptime(screen.device_boot_at):'—'} detail={screen.device_boot_at?`Since ${formatRelativeSyncTime(screen.device_boot_at)}`:undefined}/><Metric icon={Thermometer} label="Temperature" value="Normal" healthy detail="Not reported"/></div></SectionSurface>
          <SectionSurface className="p-4"><PanelTitle icon={Wifi}>Connectivity</PanelTitle><div className="mt-4 grid gap-5 md:grid-cols-[minmax(190px,.9fr)_minmax(0,1.1fr)]"><div className="space-y-2.5"><CompactRow label="Network" value="Wi-Fi"/><CompactRow label="IP address" value={screen.ip_address||'—'} mono/><CompactRow label="Signal strength" value={q.label} healthy={q.bars>=3}/><CompactRow label="Last heartbeat" value={formatRelativeSyncTime(screen.last_seen??screen.created_at??'')}/></div><div><div className="mb-2 flex justify-between text-xs text-muted-foreground"><span>Availability (recent)</span><span>{online?'100%':'—'}</span></div><LatencyGraph history={screen.latency_history??[]}/></div></div></SectionSurface>
        </div>
        <div className="space-y-4">
          <SectionSurface className="p-4"><PanelTitle icon={Tag}>Software</PanelTitle><div className="mt-4 space-y-2.5"><CompactRow label="OS version" value={screen.os_version||'—'}/><CompactRow label="App version" value={screen.app_version||'—'}/><CompactRow label="Build" value={screen.app_version_code!=null?String(screen.app_version_code):'—'}/><CompactRow label="Update status" value="Current" healthy/></div></SectionSurface>
          {!isWeb&&<SectionSurface className="p-4"><PanelTitle icon={Power}>Startup</PanelTitle><div className="mt-4 divide-y divide-border"><CompactRow label="Relaunch on boot" value={{on:'Enabled',off:'Turned off on device','no-permission':'Needs permission',unknown:'—'}[autoLaunchState(screen)]} healthy={autoLaunchState(screen)==='on'}/><CompactRow label="Set as home app" value={screen.is_home_app?'Enabled':'Not set'} healthy={screen.is_home_app===true}/></div></SectionSurface>}
        </div>
      </div>

      <SectionSurface className="overflow-hidden"><div className="flex flex-wrap items-center gap-2 border-b px-4 py-3"><div><PanelTitle icon={Activity}>Diagnostics</PanelTitle><p className="mt-0.5 text-[11px] text-muted-foreground">Latest checks</p></div><div className="ms-auto flex gap-2"><Button variant="outline" size="sm" className="gap-2" onClick={()=>window.location.reload()}><RefreshCw className="h-3.5 w-3.5"/>Refresh telemetry</Button><Button variant="outline" size="sm" className="gap-2" onClick={exportDiagnostics}><Download className="h-3.5 w-3.5"/>Export diagnostics</Button></div></div><div className="grid grid-cols-[1.5fr_.7fr_.7fr_28px] border-b px-4 py-2 text-[11px] text-muted-foreground"><span>Check</span><span>Status</span><span>Checked</span><span/></div>{checks.map(({icon:Icon,label,healthy,checked})=><div key={label} className="grid grid-cols-[1.5fr_.7fr_.7fr_28px] items-center border-b px-4 py-2.5 text-sm last:border-0"><span className="flex items-center gap-2"><Icon className="h-4 w-4 text-muted-foreground"/>{label}</span><span className={healthy?'text-emerald-600':'text-amber-600'}>{healthy?'✓ Healthy':'Needs attention'}</span><span className="text-muted-foreground">{checked}</span><RefreshCw className="h-3.5 w-3.5 text-muted-foreground"/></div>)}</SectionSurface>
    </div>
  )
}

function PanelTitle({icon:Icon,children}:{icon:LucideIcon;children:React.ReactNode}){return <h3 className="flex items-center gap-2 border-b border-[hsl(var(--border-control))] pb-3 text-sm font-semibold"><Icon className="h-4 w-4"/>{children}</h3>}
function Metric({icon:Icon,label,value,detail,healthy}:{icon:LucideIcon;label:string;value:string;detail?:string;healthy?:boolean}){return <div className="px-3 first:ps-0 last:pe-0"><p className="flex items-center gap-1.5 text-xs text-muted-foreground"><Icon className="h-3.5 w-3.5"/>{label}</p><p className={`mt-3 text-xl font-semibold ${healthy?'text-emerald-600':''}`}>{value}</p>{detail&&<p className="mt-1 text-[11px] text-muted-foreground">{detail}</p>}</div>}
function CompactRow({label,value,mono,healthy}:{label:string;value:string;mono?:boolean;healthy?:boolean}){return <div className="flex items-center justify-between gap-3 py-0.5 text-xs"><span className="text-muted-foreground">{label}</span><span className={`${mono?'font-mono ':''}${healthy?'text-emerald-600':'font-medium'} text-end`}>{value}</span></div>}

// ── Small shared building blocks ────────────────────────────────────────────
// Exported so ScreenInfoDrawer.tsx (which still uses InfoCard/InfoRow/
// SectionLabel outside the telemetry tabs — the Overview tab's summary rows)
// imports these instead of keeping a second copy.

export function InfoCard({ children }: { children: React.ReactNode }) {
  // Row-list section: the shared surface, with rows separated internally.
  return <SectionSurface className="overflow-hidden divide-y divide-border">{children}</SectionSurface>
}

export function InfoRow({ label, value, icon: Icon }: { label: string; value: React.ReactNode; icon?: LucideIcon }) {
  return (
    <div className="flex items-center justify-between gap-3 px-4 py-2.5">
      <span className="text-xs text-muted-foreground shrink-0 flex items-center gap-1.5">
        {Icon && <Icon className="w-3.5 h-3.5" />} {label}
      </span>
      <span className="text-sm font-medium text-end truncate min-w-0">{value}</span>
    </div>
  )
}

// Re-exported (see the import at the top) from settings-primitives, where it
// now lives alongside the other shared Screen Details primitives. Kept exported
// here so the three files already importing it from this module keep working.
export { SectionLabel }

// Battery icon by level/charging — the label carries the exact value, the
// icon just gives the at-a-glance state.
function batteryIcon(pct?: number | null, charging?: boolean | null): LucideIcon | undefined {
  if (pct == null) return undefined
  if (charging) return BatteryCharging
  if (pct >= 70) return BatteryFull
  if (pct >= 30) return BatteryMedium
  return BatteryLow
}

// Battery gauge — an actual SVG battery glyph (body outline + nub + a fill
// rect for the charge level), not a CSS div approximation. Proportions match
// the standard OS-status-bar battery shape (wide rounded body, small nub on
// one end). The row's text still carries the exact %; this is the
// at-a-glance read.
function MeterBar({ pct }: { pct: number }) {
  const v = Math.max(0, Math.min(100, pct))
  const tone = v >= 50 ? 'fill-emerald-500' : v >= 20 ? 'fill-amber-500' : 'fill-destructive'
  // Inner charge well is x=2.5..21.5 (19 units wide); floor the fill width so
  // a near-empty battery still shows a visible sliver instead of nothing.
  const fillWidth = Math.max((v / 100) * 19, 1.5)
  return (
    // Forced dir="ltr" on the wrapper (SVG elements have no typed `dir` prop
    // in React) — a battery glyph is a fixed physical shape (nub on one end),
    // it isn't mirrored in RTL UIs any more than an OS status-bar battery
    // indicator is, so this always renders body-then-nub regardless of page
    // direction.
    <span className="inline-flex align-middle shrink-0" dir="ltr" aria-hidden="true">
      <svg width="26" height="12" viewBox="0 0 26 12">
        <rect x="0.75" y="0.75" width="21" height="10.5" rx="2.5" className="fill-none stroke-border" strokeWidth="1.5" />
        <rect x="2.5" y="2.5" width={fillWidth} height="7" rx="1" className={tone} />
        <rect x="22.5" y="4" width="2.5" height="4" rx="1" className="fill-border" />
      </svg>
    </span>
  )
}

// Connection signal quality — derived from the heartbeat round-trip time
// (devices don't report a real RSSI, so RTT is our proxy). Four ascending
// bars, N lit by quality, plus a localized label.
function signalQuality(latency?: number | null, online?: boolean) {
  if (!online || latency == null) return { bars: 0, key: 'signalNone', label: '—', tone: 'bg-muted-foreground/40' }
  if (latency < 60)  return { bars: 4, key: 'signalExcellent', label: 'Excellent', tone: 'bg-emerald-500' }
  if (latency < 120) return { bars: 3, key: 'signalGood',      label: 'Good',      tone: 'bg-emerald-500' }
  if (latency < 250) return { bars: 2, key: 'signalFair',      label: 'Fair',      tone: 'bg-amber-500' }
  return { bars: 1, key: 'signalPoor', label: 'Poor', tone: 'bg-destructive' }
}

function SignalBars({ bars, tone }: { bars: number; tone: string }) {
  return (
    <span className="inline-flex items-end gap-[2px] h-3 align-middle">
      {[1, 2, 3, 4].map(i => (
        <span
          key={i}
          className={`w-1 rounded-sm ${i <= bars ? tone : 'bg-muted'}`}
          style={{ height: `${i * 25}%` }}
        />
      ))}
    </span>
  )
}

// ── LatencyGraph ────────────────────────────────────────────────────────────
// Sparkline of the device's rolling heartbeat-RTT window (latency_history,
// server-capped at ~30 min of 30s beats). The line breaks wherever
// consecutive samples are more than two heartbeat cycles apart — those holes
// are real missed heartbeats, not rendering artifacts.
const GAP_BREAK_MS = 90_000
const GRAPH_W = 560
const GRAPH_H = 96
const GRAPH_PAD_Y = 8

function LatencyGraph({ history }: { history: { t: string; ms: number }[] }) {
  const { t, i18n } = useTranslation()
  const [hoverIdx, setHoverIdx] = useState<number | null>(null)

  const samples = history
    .map(s => ({ at: new Date(s.t).getTime(), ms: s.ms }))
    .filter(s => Number.isFinite(s.at) && Number.isFinite(s.ms) && s.ms >= 0)
    .sort((a, b) => a.at - b.at)

  if (samples.length < 2) {
    return (
      <p className="text-xs text-muted-foreground text-center py-6">
        {t('screens.latencyGraphEmpty', 'Not enough samples yet — the graph fills in as heartbeats arrive.')}
      </p>
    )
  }

  const t0 = samples[0].at
  const t1 = samples[samples.length - 1].at
  const span  = Math.max(1, t1 - t0)
  const maxMs = Math.max(...samples.map(s => s.ms), 1)
  const x = (at: number) => ((at - t0) / span) * GRAPH_W
  const y = (ms: number) => GRAPH_H - GRAPH_PAD_Y - (ms / maxMs) * (GRAPH_H - GRAPH_PAD_Y * 2)

  const segments: (typeof samples)[] = []
  let run: typeof samples = []
  samples.forEach((s, i) => {
    if (i > 0 && s.at - samples[i - 1].at > GAP_BREAK_MS && run.length) {
      segments.push(run)
      run = []
    }
    run.push(s)
  })
  if (run.length) segments.push(run)

  const linePath = (seg: typeof samples) =>
    seg.map((s, i) => `${i === 0 ? 'M' : 'L'}${x(s.at).toFixed(1)},${y(s.ms).toFixed(1)}`).join(' ')
  const areaPath = (seg: typeof samples) =>
    `${linePath(seg)} L${x(seg[seg.length - 1].at).toFixed(1)},${GRAPH_H - GRAPH_PAD_Y} ` +
    `L${x(seg[0].at).toFixed(1)},${GRAPH_H - GRAPH_PAD_Y} Z`

  const fmtTime = (at: number) => new Date(at).toLocaleTimeString(
    i18n.language === 'ar' ? 'ar-SA' : 'en-GB',
    { hour: '2-digit', minute: '2-digit' },
  )

  const onMove = (e: React.MouseEvent<SVGSVGElement>) => {
    const rect = e.currentTarget.getBoundingClientRect()
    const at = t0 + ((e.clientX - rect.left) / Math.max(1, rect.width)) * span
    let best = 0
    samples.forEach((s, i) => { if (Math.abs(s.at - at) < Math.abs(samples[best].at - at)) best = i })
    setHoverIdx(best)
  }
  const hover = hoverIdx != null ? samples[hoverIdx] : null

  const avgMs = Math.round(samples.reduce((sum, s) => sum + s.ms, 0) / samples.length)
  const minMs = Math.min(...samples.map(s => s.ms))

  return (
    <div dir="ltr">
      <div className="relative">
        <svg
          viewBox={`0 0 ${GRAPH_W} ${GRAPH_H}`}
          className="w-full h-24 block"
          preserveAspectRatio="none"
          role="img"
          aria-label={t('screens.connectionGraph', 'Ping — recent heartbeats')}
          onMouseMove={onMove}
          onMouseLeave={() => setHoverIdx(null)}
        >
          <line
            x1={0} x2={GRAPH_W} y1={GRAPH_H - GRAPH_PAD_Y} y2={GRAPH_H - GRAPH_PAD_Y}
            className="stroke-border" strokeWidth={1} vectorEffect="non-scaling-stroke"
          />
          {segments.map((seg, i) => (
            <g key={i}>
              {seg.length > 1 && <path d={areaPath(seg)} className="fill-primary/10" stroke="none" />}
              <path
                d={linePath(seg)}
                className="stroke-primary"
                fill="none"
                strokeWidth={2}
                strokeLinejoin="round"
                strokeLinecap="round"
                vectorEffect="non-scaling-stroke"
              />
              {seg.length === 1 && (
                <circle cx={x(seg[0].at)} cy={y(seg[0].ms)} r={3} className="fill-primary" />
              )}
            </g>
          ))}
          {hover && (
            <circle
              cx={x(hover.at)} cy={y(hover.ms)} r={4}
              className="fill-primary stroke-card"
              strokeWidth={2}
              vectorEffect="non-scaling-stroke"
            />
          )}
        </svg>
        {hover && (
          <div
            className="absolute -top-1 -translate-y-full -translate-x-1/2 pointer-events-none rounded-md border border-border bg-popover text-popover-foreground shadow-md px-2 py-1 text-[11px] tabular-nums whitespace-nowrap"
            style={{ left: `${((hover.at - t0) / span) * 100}%` }}
          >
            <span className="font-semibold">{hover.ms} ms</span>
            <span className="text-muted-foreground"> · {fmtTime(hover.at)}</span>
          </div>
        )}
      </div>
      <div className="mt-1.5 flex items-center justify-between text-[11px] text-muted-foreground tabular-nums">
        <span>{fmtTime(t0)}</span>
        <span>
          {t('screens.latencySummary', {
            defaultValue: 'min {{min}} · avg {{avg}} · max {{max}} ms',
            min: minMs, avg: avgMs, max: maxMs,
          })}
        </span>
        <span>{fmtTime(t1)}</span>
      </div>
    </div>
  )
}

// Live uptime from the stored boot instant — "12d 4h" / "3h 24m" / "45m".
// Computed at render time so it never goes stale between heartbeats.
function fmtUptime(bootAtIso: string): string {
  const ms = Date.now() - new Date(bootAtIso).getTime()
  if (!Number.isFinite(ms) || ms < 0) return '—'
  const mins = Math.floor(ms / 60_000)
  const d = Math.floor(mins / 1440)
  const h = Math.floor((mins % 1440) / 60)
  const m = mins % 60
  if (d > 0) return `${d}d ${h}h`
  if (h > 0) return `${h}h ${m}m`
  return `${m}m`
}


// ── Timezone detail ─────────────────────────────────────────────────────────
// A screen's zone is not trivia: dayparts, scheduled playlists and delivery
// windows all resolve in the SCREEN's local time, so an operator in Riyadh
// scheduling a Cairo screen needs to know what "18:00" means there. The bare
// IANA name ("Africa/Cairo") does not answer that, so this adds the parts that
// do — live local clock, UTC offset, the delta from the viewer's own zone, and
// whether daylight saving is currently in effect.
//
// All derived client-side from the IANA name via Intl; no backend field and no
// timezone database is shipped. The device reports the zone in its heartbeat.
function TimezoneDetail({ timezone }: { timezone?: string | null }) {
  const { t, i18n } = useTranslation()
  // Ticks the clock. 1s rather than 60s so the seconds field is not visibly
  // wrong — an operator comparing it against a wall clock will notice.
  const [now, setNow] = useState(() => new Date())
  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 1000)
    return () => window.clearInterval(id)
  }, [])

  const info = useMemo(() => {
    if (!timezone) return null
    try {
      const localTime = new Intl.DateTimeFormat(i18n.language, {
        timeZone: timezone, hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
      }).format(now)
      const localDate = new Intl.DateTimeFormat(i18n.language, {
        timeZone: timezone, weekday: 'short', day: 'numeric', month: 'short',
      }).format(now)
      // Short offset ("GMT+3") straight from Intl, so no hand-rolled DST maths.
      const offsetName = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone, timeZoneName: 'shortOffset',
      }).formatToParts(now).find(p => p.type === 'timeZoneName')?.value ?? ''

      // Offset in minutes, both for the screen and for whoever is looking, by
      // formatting the same instant in both zones and differencing. Avoids
      // assuming whole-hour offsets — India, Nepal and Iran are not.
      const minutesIn = (tz: string) => {
        const parts = new Intl.DateTimeFormat('en-US', {
          timeZone: tz, hour12: false,
          year: 'numeric', month: '2-digit', day: '2-digit',
          hour: '2-digit', minute: '2-digit', second: '2-digit',
        }).formatToParts(now).reduce<Record<string, string>>((a, p) => (a[p.type] = p.value, a), {})
        const asUtc = Date.UTC(
          +parts.year, +parts.month - 1, +parts.day,
          +parts.hour % 24, +parts.minute, +parts.second,
        )
        return Math.round((asUtc - Math.floor(now.getTime() / 1000) * 1000) / 60000)
      }
      const viewerTz = Intl.DateTimeFormat().resolvedOptions().timeZone
      const deltaMin = minutesIn(timezone) - minutesIn(viewerTz)

      // DST: compare this instant's offset against January's and July's. The
      // smaller of the two is standard time, so being above it means DST is
      // currently in effect. Works for both hemispheres.
      const offsetAt = (d: Date) => {
        const parts = new Intl.DateTimeFormat('en-US', {
          timeZone: timezone, hour12: false,
          year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
        }).formatToParts(d).reduce<Record<string, string>>((a, p) => (a[p.type] = p.value, a), {})
        return (Date.UTC(+parts.year, +parts.month - 1, +parts.day, +parts.hour % 24, +parts.minute)
          - Math.floor(d.getTime() / 60000) * 60000) / 60000
      }
      const y = now.getUTCFullYear()
      const jan = offsetAt(new Date(Date.UTC(y, 0, 1)))
      const jul = offsetAt(new Date(Date.UTC(y, 6, 1)))
      const observesDst = jan !== jul
      const inDst = observesDst && offsetAt(now) > Math.min(jan, jul)

      return { localTime, localDate, offsetName, deltaMin, viewerTz, observesDst, inDst }
    } catch {
      // An unrecognised zone string (old app build, corrupted heartbeat) must
      // not take the whole panel down — fall back to showing nothing extra.
      return null
    }
  }, [timezone, now, i18n.language])

  if (!timezone) return <InfoCard><InfoRow label={t('screens.timezone', 'Timezone')} value="—" /></InfoCard>
  if (!info) return <InfoCard><InfoRow label={t('screens.timezone', 'Timezone')} value={<span className="font-mono">{timezone}</span>} /></InfoCard>

  const h = Math.floor(Math.abs(info.deltaMin) / 60)
  const m = Math.abs(info.deltaMin) % 60
  const delta = info.deltaMin === 0
    ? t('screens.tzSameAsYou', 'Same as you')
    : t('screens.tzOffsetFromYou', '{{hm}} {{dir}} of you', {
        hm: m ? `${h}h ${m}m` : `${h}h`,
        dir: info.deltaMin > 0 ? t('screens.tzAhead', 'ahead') : t('screens.tzBehind', 'behind'),
      })

  return (
    <InfoCard>
      <InfoRow label={t('screens.tzZone', 'Zone')} value={<span className="font-mono">{timezone}</span>} icon={Globe} />
      <InfoRow
        label={t('screens.tzLocalTime', 'Local time')}
        value={<span className="font-mono tabular-nums">{info.localTime} <span className="text-muted-foreground font-sans">{info.localDate}</span></span>}
        icon={Clock}
      />
      <InfoRow label={t('screens.tzUtcOffset', 'UTC offset')} value={<span className="font-mono">{info.offsetName}</span>} />
      <InfoRow label={t('screens.tzVsYou', 'Vs. your time')} value={delta} />
      <InfoRow
        label={t('screens.tzDst', 'Daylight saving')}
        value={
          !info.observesDst
            ? t('screens.tzDstNone', 'Not observed')
            : info.inDst
              ? t('screens.tzDstOn', 'In effect now')
              : t('screens.tzDstOff', 'Observed — not in effect')
        }
      />
    </InfoCard>
  )
}
